import { readVaultTenantAssignmentsFromStorage } from './vault-tenant-assignments.storage';

const UUID_V4ISH =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const TENANT_CLAIM_KEYS = [
  'tenant_id',
  'tenantId',
  'active_tenant_id',
  'activeTenantId',
  'datavault_tenant_id',
  'datavaultTenantId',
  'vault_tenant_id',
  'vaultTenantId',
] as const;

/** UUID de tenant Datavault (no ids Nebula tipo `7` o `7:1`). */
export function normalizeVaultTenantId(value: string | null | undefined): string {
  const tenantId = (value || '').trim();
  if (!tenantId) return '';
  return UUID_V4ISH.test(tenantId) ? tenantId : '';
}

export function isVaultUuid(value: string | null | undefined): boolean {
  return UUID_V4ISH.test((value || '').trim());
}

/** Lee `tenant_id` del JWT / usuario (post jwt-select-company). */
export function extractVaultTenantIdFromRecord(
  record: Record<string, unknown> | null | undefined,
): string {
  if (!record) return '';
  for (const key of TENANT_CLAIM_KEYS) {
    const raw = record[key];
    if (typeof raw === 'string' && raw.trim()) {
      return normalizeVaultTenantId(raw) || raw.trim();
    }
  }
  for (const key of ['companyId', 'company_id']) {
    const id = normalizeVaultTenantId(
      record[key] != null ? String(record[key]).trim() : '',
    );
    if (id) return id;
  }
  const user = (record['User'] ?? record['user']) as Record<string, unknown> | undefined;
  if (user && user !== record) {
    return extractVaultTenantIdFromRecord(user);
  }
  for (const nestedKey of ['company', 'Company', 'customer', 'Customer', 'tenant', 'Tenant']) {
    const nested = record[nestedKey];
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const fromNested = extractVaultTenantIdFromRecord(nested as Record<string, unknown>);
      if (fromNested) return fromNested;
    }
  }
  return '';
}

export function extractVaultTenantIdFromJwtPayload(
  payload: Record<string, unknown> | null | undefined,
): string {
  if (!payload) return '';
  const top = extractVaultTenantIdFromRecord(payload);
  if (top) return top;
  const user = (payload['User'] ?? payload['user']) as Record<string, unknown> | undefined;
  return extractVaultTenantIdFromRecord(user);
}

/**
 * UUID de empresa Vault desde filas de asignación (`auth/me`, `my-tenants`).
 * No usar `id` cuando la fila trae `role`: en ese caso `id` es user_tenant, no tenant.
 */
export function readVaultTenantIdFromAssignmentRow(
  row: Record<string, unknown> | null | undefined,
): string {
  if (!row) return '';
  const explicit = String(row['tenant_id'] ?? row['tenantId'] ?? '').trim();
  if (explicit) {
    return normalizeVaultTenantId(explicit) || explicit;
  }
  if (String(row['role'] ?? '').trim()) {
    return '';
  }
  const id = String(row['id'] ?? '').trim();
  if (!id) return '';
  const hasCompanyLabel =
    row['name'] != null ||
    row['tenant_name'] != null ||
    row['tenantName'] != null ||
    row['tenant_slug'] != null ||
    row['tenantSlug'] != null;
  if (hasCompanyLabel) {
    return normalizeVaultTenantId(id) || id;
  }
  return '';
}

function tenantIdFromTenantLinkRow(row: Record<string, unknown>): string {
  return readVaultTenantIdFromAssignmentRow(row);
}

/** Lista `tenants` de `/api/auth/me`, `my-tenants` o jwt-get. */
export function extractVaultTenantIdFromTenantsList(tenants: unknown): string {
  if (!Array.isArray(tenants) || tenants.length === 0) {
    return '';
  }
  if (typeof localStorage !== 'undefined') {
    const saved = (
      localStorage.getItem('tenant_id') ||
      localStorage.getItem('tenantId') ||
      ''
    ).trim();
    if (saved) {
      for (const entry of tenants) {
        if (!entry || typeof entry !== 'object') continue;
        const tid = tenantIdFromTenantLinkRow(entry as Record<string, unknown>);
        if (tid && tid === saved) return tid;
      }
    }
  }
  if (tenants.length === 1) {
    const only = tenants[0];
    if (only && typeof only === 'object') {
      const tid = tenantIdFromTenantLinkRow(only as Record<string, unknown>);
      if (tid) return tid;
    }
  }
  for (const entry of tenants) {
    if (!entry || typeof entry !== 'object') continue;
    const tid = tenantIdFromTenantLinkRow(entry as Record<string, unknown>);
    if (tid) return tid;
  }
  return '';
}

/** Persiste tenant activo en `localStorage` y `vault_server_context`. */
export function persistVaultTenantScopeToStorage(tenantId: string): void {
  const id = normalizeVaultTenantId(tenantId);
  if (!id || typeof window === 'undefined') return;

  const previous =
    localStorage.getItem('tenant_id')?.trim() ||
    localStorage.getItem('tenantId')?.trim() ||
    '';
  if (previous === id) {
    return;
  }

  localStorage.setItem('tenant_id', id);
  try {
    const raw = localStorage.getItem('vault_server_context');
    const parsed = raw ? (JSON.parse(raw) as { tenantId?: string }) : {};
    parsed.tenantId = id;
    localStorage.setItem('vault_server_context', JSON.stringify(parsed));
  } catch {
    localStorage.setItem('vault_server_context', JSON.stringify({ tenantId: id }));
  }

  try {
    const udRaw = localStorage.getItem('user_data');
    if (udRaw) {
      const ud = JSON.parse(udRaw) as Record<string, unknown>;
      ud['tenant_id'] = id;
      localStorage.setItem('user_data', JSON.stringify(ud));
    }
  } catch {
    /* ignore */
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('vault-session-updated'));
  }
}

function decodeAccessTokenPayload(): Record<string, unknown> | null {
  if (typeof localStorage === 'undefined') return null;
  const token = localStorage.getItem('access_token');
  if (!token || typeof token !== 'string') return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = atob(padded);
    const parsed: unknown = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Elimina `tenant_id` corrupto en localStorage (p. ej. `/` o id Nebula no UUID). */
export function sanitizeInvalidVaultTenantScopeInStorage(): void {
  if (typeof localStorage === 'undefined') return;
  for (const key of ['tenant_id', 'tenantId', 'selected_tenant_id'] as const) {
    const raw = localStorage.getItem(key)?.trim();
    if (raw && !normalizeVaultTenantId(raw)) {
      localStorage.removeItem(key);
    }
  }
}

/** Re-sincroniza `tenant_id` desde el JWT en `localStorage` (sin inyectar AuthService). */
export function syncVaultTenantScopeFromAccessToken(): void {
  if (typeof window === 'undefined') return;

  const hasVaultAssignment = readVaultTenantAssignmentsFromStorage().some((row) =>
    normalizeVaultTenantId(row.tenant_id),
  );
  if (hasVaultAssignment) return;

  const existingScope = normalizeVaultTenantId(
    localStorage.getItem('tenant_id') || localStorage.getItem('tenantId'),
  );
  if (existingScope) return;

  const payload = decodeAccessTokenPayload();
  if (!payload) return;
  const user = (payload['User'] ?? payload['user']) as Record<string, unknown> | undefined;
  const scope =
    extractVaultTenantIdFromJwtPayload(payload) ||
    extractVaultTenantIdFromRecord(user);
  if (scope) {
    persistVaultTenantScopeToStorage(scope);
  }
}

/** JWT → localStorage → user_data (sin listas my-tenants). */
export function resolveVaultActiveTenantIdFromTokenAndStorage(): string {
  const payload = decodeAccessTokenPayload();
  if (payload) {
    const fromJwt = extractVaultTenantIdFromJwtPayload(payload);
    if (fromJwt) return fromJwt;
  }

  if (typeof localStorage !== 'undefined') {
    const fromLs = normalizeVaultTenantId(
      localStorage.getItem('tenant_id') ||
        localStorage.getItem('tenantId') ||
        localStorage.getItem('selected_tenant_id'),
    );
    if (fromLs) return fromLs;
  }

  try {
    const udRaw = localStorage.getItem('user_data');
    if (udRaw) {
      const ud = JSON.parse(udRaw) as Record<string, unknown>;
      for (const key of ['tenant_id', 'tenantId', 'active_tenant_id'] as const) {
        const v = ud[key];
        if (typeof v === 'string' && v.trim()) {
          return normalizeVaultTenantId(v) || v.trim();
        }
      }
    }
  } catch {
    /* ignore */
  }

  try {
    const rawContext = localStorage.getItem('vault_server_context');
    if (rawContext) {
      const parsed = JSON.parse(rawContext) as { tenantId?: string };
      const fromCtx = normalizeVaultTenantId(parsed.tenantId);
      if (fromCtx) return fromCtx;
    }
  } catch {
    /* ignore */
  }

  return '';
}
